import React, { useState } from 'react';
import { ShieldCheck, ShieldAlert, Lock, ScrollText, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { VoiceLayout } from './VoiceLayout';
import { AI_COMPLIANCE_RULES, AI_AUDIT_LOG } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { useConfirm } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function VoiceCompliance() {
  usePageTitle('AI Voice Compliance');
  const confirm = useConfirm();
  const [rules, setRules] = useState(AI_COMPLIANCE_RULES);

  const toggle = async (catId, itemId) => {
    const item = rules.find((c) => c.category === catId).items.find((i) => i.id === itemId);
    if (item.enabled) {
      const ok = await confirm({
        title: `Disable "${item.label}"?`,
        description: 'This is a SAMA-aligned guardrail. Disabling it removes an enforcement check from every live AI conversation. The change will be logged to the audit trail.',
        impactSummary: [
          { label: 'Rule', value: item.label },
          { label: 'Current state', value: item.value },
          { label: 'Logged to audit', value: 'Yes' },
        ],
        confirmLabel: 'Disable rule',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    setRules((rs) => rs.map((c) => c.category !== catId ? c : {
      ...c,
      items: c.items.map((i) => i.id !== itemId ? i : { ...i, enabled: !i.enabled }),
    }));
    toast.message(`${item.label} ${item.enabled ? 'disabled' : 'enabled'}`);
  };

  return (
    <VoiceLayout
      title="AI voice — compliance & guardrails"
      subtitle="SAMA-aligned enforcement layer for every live AI conversation. Toggling a rule is logged to the audit trail and visibly impacts downstream call evaluations."
    >
      <div className="grid lg:grid-cols-2 gap-4">
        {rules.map((cat) => (
          <section key={cat.category} className="surface-card p-4" data-testid={`compliance-${cat.category.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />{cat.category}</p>
              <Badge variant="outline" className="text-[10px]">{cat.items.filter((i) => i.enabled).length} / {cat.items.length} active</Badge>
            </div>
            <div className="space-y-2">
              {cat.items.map((it) => (
                <div key={it.id} className="flex items-center justify-between p-2.5 rounded-md border border-border bg-muted/30">
                  <div className="pr-3">
                    <p className="text-[13px] font-medium leading-tight" id={`${it.id}-label`}>{it.label}</p>
                    <p className="text-[11px] text-muted-foreground" id={`${it.id}-desc`}>{it.value}</p>
                  </div>
                  <Switch
                    checked={it.enabled}
                    onCheckedChange={() => toggle(cat.category, it.id)}
                    aria-label={`${it.label} — ${it.enabled ? 'enabled' : 'disabled'}`}
                    aria-describedby={`${it.id}-desc`}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="surface-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-primary" aria-hidden="true" />
          <p className="text-sm font-semibold">Audit log</p>
          <Button variant="outline" size="sm" className="ml-auto h-7 text-[11px]"><Filter className="h-3 w-3 mr-1" aria-hidden="true" />Filter</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th scope="col" className="text-left px-3 py-2 font-medium">ID</th>
              <th scope="col" className="text-left px-3 py-2 font-medium">Time</th>
              <th scope="col" className="text-left px-3 py-2 font-medium">Actor</th>
              <th scope="col" className="text-left px-3 py-2 font-medium">Action</th>
              <th scope="col" className="text-left px-3 py-2 font-medium">Target</th>
              <th scope="col" className="text-left px-3 py-2 font-medium">Severity</th>
            </tr>
          </thead>
          <tbody>
            {AI_AUDIT_LOG.map((e) => (
              <tr key={e.id} className="border-t border-border hover:bg-muted/40">
                <td className="px-3 py-2 font-mono-num text-[12px]">{e.id}</td>
                <td className="px-3 py-2 text-[12px] text-muted-foreground">{e.at}</td>
                <td className="px-3 py-2 text-[12px]">{e.actor}</td>
                <td className="px-3 py-2 text-[12px]">{e.action}</td>
                <td className="px-3 py-2 text-[12px] text-muted-foreground">{e.target}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={cn('text-[10px]',
                    e.severity === 'danger' && 'border-destructive/40 bg-destructive/10 text-destructive',
                    e.severity === 'warn' && 'border-warning/40 bg-warning/10 text-warning',
                    e.severity === 'info' && 'border-info/40 bg-info/10 text-info',
                  )}>{e.severity}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </VoiceLayout>
  );
}
